import { useRef, useState } from 'react'
import type { PointerEvent } from 'react'

interface DragResizeOptions {
  axis: 'x' | 'y'
  min: number
  max: number
  // Size used before the user ever resizes; undefined lets the element keep its
  // natural/CSS-driven size until the first drag.
  defaultSize?: number
  // Persists the user-chosen size across sessions; omit for session-only resizing.
  storageKey?: string
  // Element resized imperatively while dragging (panel container, textarea).
  getTarget: () => HTMLElement | null
}

const readStoredSize = (key: string) => {
  try {
    const raw = window.localStorage.getItem(key)
    const value = raw === null ? NaN : Number(raw)
    return Number.isFinite(value) ? value : undefined
  } catch {
    return undefined
  }
}

const storeSize = (key: string, size: number) => {
  try {
    window.localStorage.setItem(key, String(size))
  } catch {
    // Storage can be unavailable (private mode) — resizing still works for the session.
  }
}

// Drag-to-resize for a handle sitting on the leading edge of its box (left edge
// of a right-docked panel, top edge of a bottom input) — dragging away from the
// box grows it. Spread `handleProps` on the handle element. While dragging, the
// target is sized imperatively (no React re-render per pointermove); the final
// size is committed to state + localStorage on release and restored on mount.
export function useDragResize({
  axis,
  min,
  max,
  defaultSize,
  storageKey,
  getTarget,
}: DragResizeOptions) {
  const clamp = (value: number) => Math.min(max, Math.max(min, value))

  const [size, setSize] = useState<number | undefined>(() => {
    const stored = storageKey === undefined ? undefined : readStoredSize(storageKey)
    return stored === undefined ? defaultSize : clamp(stored)
  })
  const [isResizing, setIsResizing] = useState(false)

  const dragRef = useRef<{ startPos: number; startSize: number; lastSize: number } | null>(null)

  const positionOf = (event: PointerEvent<HTMLElement>) =>
    axis === 'x' ? event.clientX : event.clientY

  const applySize = (value: number) => {
    const target = getTarget()
    if (!target) return
    if (axis === 'x') target.style.width = `${value}px`
    else target.style.height = `${value}px`
  }

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    const target = getTarget()
    if (!target) return
    // Stops text selection / native drag from starting alongside the resize.
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    const rect = target.getBoundingClientRect()
    const startSize = axis === 'x' ? rect.width : rect.height
    dragRef.current = { startPos: positionOf(event), startSize, lastSize: startSize }
    setIsResizing(true)
  }

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    if (!drag) return
    // Leading-edge handle: moving toward smaller coordinates grows the box.
    const next = clamp(drag.startSize + (drag.startPos - positionOf(event)))
    drag.lastSize = next
    applySize(next)
  }

  const endDrag = (event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    if (!drag) return
    dragRef.current = null
    setIsResizing(false)
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    const finalSize = Math.round(clamp(drag.lastSize))
    setSize(finalSize)
    if (storageKey !== undefined) storeSize(storageKey, finalSize)
  }

  return {
    size,
    isResizing,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  }
}
